import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Mail, Trash2, AlertCircle, Crown, Shield, User as UserIcon,
  Plus, Copy, X, Check, Link as LinkIcon, Clock, Upload, Image as ImageIcon,
  Slack as SlackIcon, Send, AlertTriangle, UserMinus, RotateCcw,
  ChevronDown, ChevronRight, Loader,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useOrg } from '../context/OrgContext';
import './OrgSettings.css';
import { absoluteMediaUrl } from '../media';
import UserAvatar from './UserAvatar';

/**
 * /org/settings — organization page with two sections:
 *   1. Header: org name, domain(s), owner, member count
 *   2. Members: list of all org memberships with role + actions
 *
 * Role-based UI: regular members see the list read-only.
 * Admins and owners can change roles and remove members
 * (except the owner, who is protected server-side too).
 *
 * Invite generation lands in the next delivery — placeholder section here.
 *
 * WR-020 adds Integrations → Slack, admin/owner only. The webhook URL is
 * write-only: the server never sends it back, so this page can show the
 * masked hint and offer to replace it, and nothing more. There is no state
 * here that holds the secret after the request that sets it.
 *
 * WR-023 makes "Remove from organization" a real removal. Two additions:
 *
 *   · a RemoveMemberDialog that fetches the impact (projects, open tasks,
 *     open subtasks, live tokens) before anything is written, and requires
 *     the person's name to be typed to confirm. The old flow was a
 *     window.confirm() whose text — "they just lose access to this
 *     workspace" — was not true: nothing revoked access at all.
 *   · a collapsed "Former members" section listing deactivated people with
 *     a Reactivate action. They are NOT left in the main list greyed out:
 *     that list answers "who is in this organization", and a removed person
 *     is not, which is the category error that let the production bug go
 *     unnoticed for nine days.
 */

function RoleIcon({ role }) {
  if (role === 'owner')  return <Crown   size={12} />;
  if (role === 'admin')  return <Shield  size={12} />;
  return <UserIcon size={12} />;
}

/*
 * ── RemoveMemberDialog (WR-023 §3) ───────────────────────────────────
 *
 * Shows what the removal will actually do before it is possible to confirm.
 *
 * The counts come from GET /orgs/:id/members/:userId/removal-impact, which
 * is served by the same service function that performs the cascade — so the
 * dialog cannot promise "7 open tasks" and then unassign nine. It loads on
 * open and the confirm button stays disabled until it arrives; an admin
 * approving a destructive action against "…" is an admin approving nothing.
 *
 * Confirmation is the person's name typed out, not an OK button. Removal
 * revokes a colleague's access and unassigns their work, it is easy to fire
 * at the wrong row in a list of similar-looking rows, and the typed name is
 * the cheapest way to make the target explicit.
 *
 * The reassign-to picker is offered because the impact query already tells
 * us the work exists and the members list is already loaded, so it costs one
 * <select> and one request field. Default is plain unassign.
 */
function RemoveMemberDialog({ org, membership, members, onCancel, onConfirm }) {
  const target = membership.user;

  const [impact, setImpact]   = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [typed, setTyped]     = useState('');
  const [reassignTo, setReassignTo] = useState('');
  const [busy, setBusy]       = useState(false);
  const [busyErr, setBusyErr] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await axios.get(
          `/orgs/${org._id}/members/${target._id}/removal-impact`
        );
        if (live) setImpact(res.data);
      } catch (e) {
        if (live) setLoadErr(e.response?.data?.error || 'Could not load the impact of this removal.');
      }
    })();
    return () => { live = false; };
  }, [org._id, target._id]);

  // Case- and whitespace-insensitive: this is a deliberateness check, not a
  // spelling test, and an admin who typed the right name with a stray space
  // has demonstrated everything it is there to demonstrate.
  const nameMatches = typed.trim().toLowerCase() === String(target.name).trim().toLowerCase();

  const hasWork = !!impact && (impact.openTaskCount > 0 || impact.openSubtaskCount > 0);

  // Anyone still in the org except the person being removed.
  const reassignCandidates = (members || [])
    .filter(m => String(m.user?._id) !== String(target._id))
    .map(m => m.user)
    .filter(Boolean);

  const submit = async () => {
    setBusy(true);
    setBusyErr(null);
    try {
      await onConfirm(membership, reassignTo || null);
    } catch (e) {
      setBusyErr(e.response?.data?.error || 'Failed to remove this member.');
      setBusy(false);
    }
  };

  const blocked = impact?.blocked || null;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div
        className="modal-content remove-member-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-member-title"
      >
        <div className="invite-modal-header">
          <h3 id="remove-member-title" className="remove-member-title">
            <UserMinus size={18} /> Remove from organization
          </h3>
          <button className="btn btn-ghost" onClick={onCancel} aria-label="Close" style={{ padding: 6 }}>
            <X size={16} />
          </button>
        </div>

        <div className="remove-member-body">
          <div className="remove-member-target">
            <UserAvatar user={target} className="member-avatar" />
            <div>
              <div className="member-name">{target.name}</div>
              <div className="member-email">{target.email}</div>
            </div>
          </div>

          {loadErr && (
            <div className="remove-member-error">
              <AlertCircle size={14} /> {loadErr}
            </div>
          )}

          {!impact && !loadErr && (
            <div className="remove-member-loading">
              <Loader size={14} className="spin" /> Checking what this will change…
            </div>
          )}

          {blocked && (
            <div className="remove-member-error">
              <AlertCircle size={14} /> {blocked}
            </div>
          )}

          {impact && !blocked && (
            <>
              {/* The sentence WR-023 asks for, built from real counts. */}
              <p className="remove-member-summary">
                <strong>{impact.user.name}</strong> is a member of{' '}
                <strong>{impact.projectCount}</strong>{' '}
                {impact.projectCount === 1 ? 'project' : 'projects'} and assigned to{' '}
                <strong>{impact.openTaskCount}</strong> open{' '}
                {impact.openTaskCount === 1 ? 'task' : 'tasks'} and{' '}
                <strong>{impact.openSubtaskCount}</strong>{' '}
                {impact.openSubtaskCount === 1 ? 'subtask' : 'subtasks'}.
                {hasWork && !reassignTo && ' Removing them unassigns that work.'}
                {hasWork && reassignTo && ' Removing them reassigns that work.'}
              </p>

              {impact.projectCount > 0 && (
                <ul className="remove-member-projects">
                  {impact.projects.map(p => <li key={p._id}>{p.name}</li>)}
                </ul>
              )}

              <ul className="remove-member-effects">
                <li>They can no longer sign in, and any open session is cut off immediately.</li>
                {(impact.activePats > 0 || impact.activeGrants > 0) && (
                  <li>
                    <strong>{impact.activePats}</strong> access{' '}
                    {impact.activePats === 1 ? 'token' : 'tokens'} and{' '}
                    <strong>{impact.activeGrants}</strong> connected{' '}
                    {impact.activeGrants === 1 ? 'app' : 'apps'} stop working.
                  </li>
                )}
                <li>Their comments, mentions and folios stay exactly as they are.</li>
                <li>Finished and archived work keeps their name on it.</li>
                <li>This can be undone from “Former members”, but memberships and tokens are not restored.</li>
              </ul>

              {impact.ownedProjectCount > 0 && (
                <div className="remove-member-warning">
                  <AlertTriangle size={14} />
                  <div>
                    They <strong>own</strong>{' '}
                    {impact.ownedProjects.map(p => p.name).join(', ')}. Project
                    ownership is not transferred, and only an owner can edit a
                    project’s settings — so nobody will be able to change{' '}
                    {impact.ownedProjectCount === 1 ? 'that project' : 'those projects'} afterwards.
                    Transfer ownership first if that matters.
                  </div>
                </div>
              )}

              {hasWork && reassignCandidates.length > 0 && (
                <label className="remove-member-field">
                  <span>Reassign their open work to (optional)</span>
                  <select value={reassignTo} onChange={e => setReassignTo(e.target.value)}>
                    <option value="">Leave it unassigned</option>
                    {reassignCandidates.map(u => (
                      <option key={u._id} value={u._id}>{u.name}</option>
                    ))}
                  </select>
                </label>
              )}

              <label className="remove-member-field">
                <span>Type <strong>{target.name}</strong> to confirm</span>
                <input
                  type="text"
                  value={typed}
                  onChange={e => setTyped(e.target.value)}
                  placeholder={target.name}
                  autoFocus
                  autoComplete="off"
                />
              </label>

              {busyErr && (
                <div className="remove-member-error">
                  <AlertCircle size={14} /> {busyErr}
                </div>
              )}
            </>
          )}
        </div>

        <div className="invite-modal-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={submit}
            disabled={!impact || !!blocked || !nameMatches || busy}
          >
            {busy ? 'Removing…' : 'Remove from organization'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrgSettings({ user }) {
  const { currentOrg, currentMembership } = useOrg();

  const [org, setOrg] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Invite UI state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  // Logo upload state
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);

  // Slack integration (WR-020) — admin-only, so it is fetched only when the
  // caller is one; a member's request would 403 and blank the page.
  const [slack, setSlack] = useState(null);

  // Removal + former members (WR-023). Both admin-only surfaces.
  const [removing, setRemoving] = useState(null);       // the membership row being removed
  const [formerMembers, setFormerMembers] = useState([]);
  const [showFormer, setShowFormer] = useState(false);
  const [reactivating, setReactivating] = useState(null);

  const isOwner = currentMembership?.role === 'owner';
  const isAdminPlus = isOwner || currentMembership?.role === 'admin';

  useEffect(() => {
    if (!currentOrg) return;
    fetchAll();
  }, [currentOrg, isAdminPlus]);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const requests = [
        axios.get(`/orgs/${currentOrg._id}`),
        axios.get(`/orgs/${currentOrg._id}/members`),
      ];
      if (isAdminPlus) {
        requests.push(axios.get(`/orgs/${currentOrg._id}/invites`));
        requests.push(axios.get(`/orgs/${currentOrg._id}/integrations/slack`));
        requests.push(axios.get(`/orgs/${currentOrg._id}/former-members`));
      }
      const responses = await Promise.all(requests);
      setOrg(responses[0].data);
      setMembers(responses[1].data);
      if (isAdminPlus) {
        setInvites(responses[2].data);
        setSlack(responses[3].data);
        setFormerMembers(responses[4].data);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load organization settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (membership, newRole) => {
    try {
      await axios.put(
        `/orgs/${currentOrg._id}/members/${membership.user._id}`,
        { role: newRole }
      );
      setMembers(prev => prev.map(m =>
        m._id === membership._id ? { ...m, role: newRole } : m
      ));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to update role.');
    }
  };

  /*
   * WR-023: removal is confirmed in RemoveMemberDialog, which fetches the
   * impact first. This just commits it and reconciles local state.
   *
   * `reassignTo` is optional — null means unassign their open work.
   */
  const handleRemoveConfirmed = async (membership, reassignTo) => {
    const target = membership.user;
    const res = await axios.delete(
      `/orgs/${currentOrg._id}/members/${target._id}`,
      { data: reassignTo ? { reassignTo } : {} },
    );

    setMembers(prev => prev.filter(m => m._id !== membership._id));
    setRemoving(null);

    // They are now a former member, so they belong in that section. Refetch
    // rather than synthesising the row: the server owns deactivatedAt and
    // deactivatedBy, and guessing them would show the wrong thing until the
    // next page load.
    try {
      const former = await axios.get(`/orgs/${currentOrg._id}/former-members`);
      setFormerMembers(former.data);
    } catch { /* the section just stays stale until reload */ }

    return res.data;
  };

  const handleReactivate = async (formerMember) => {
    if (!window.confirm(
      `Reactivate ${formerMember.name}?\n\n` +
      `This restores their ability to sign in and NOTHING else. Their project ` +
      `memberships, task assignments and access tokens are not restored — ` +
      `send them an invite to add them back to the organization.`
    )) return;

    setReactivating(formerMember._id);
    try {
      await axios.post(`/orgs/${currentOrg._id}/members/${formerMember._id}/reactivate`);
      setFormerMembers(prev => prev.filter(f => String(f._id) !== String(formerMember._id)));
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to reactivate.');
    } finally {
      setReactivating(null);
    }
  };

  // ── Invites ──────────────────────────────────────────────────────

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  const handleGenerate = async ({ email, role, expiresInDays }) => {
    const res = await axios.post(`/orgs/${currentOrg._id}/invites`, {
      email: email || null,
      role,
      expiresInDays,
    });
    setInvites(prev => [res.data, ...prev]);
    setJustGenerated(res.data);
    setShowInviteModal(false);
  };

  const handleRevoke = async (token) => {
    if (!window.confirm('Revoke this invite? Anyone holding the link will no longer be able to use it.')) return;
    try {
      await axios.delete(`/orgs/${currentOrg._id}/invites/${token}`);
      setInvites(prev => prev.filter(i => i.token !== token));
      if (justGenerated?.token === token) setJustGenerated(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to revoke invite.');
    }
  };

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(prev => prev === token ? null : prev), 1800);
    } catch {
      // Older browsers / non-secure context: fall back to prompt
      window.prompt('Copy this invite link:', inviteUrl(token));
    }
  };

  // ── Logo ─────────────────────────────────────────────────────────

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                       // reset so picking same file again still fires
    if (!file) return;
    setLogoError(null);
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      const res = await axios.put(
        `/orgs/${currentOrg._id}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setOrg(prev => ({ ...prev, logo: res.data.logo }));
      // Reload so the sidebar picks up the new logo (cached in OrgContext from /auth/me).
      setTimeout(() => window.location.reload(), 400);
    } catch (ex) {
      setLogoError(ex.response?.data?.error || 'Failed to upload logo.');
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Remove the organization logo?')) return;
    setLogoError(null);
    setLogoUploading(true);
    try {
      await axios.delete(`/orgs/${currentOrg._id}/logo`);
      setOrg(prev => ({ ...prev, logo: '' }));
      setTimeout(() => window.location.reload(), 400);
    } catch (ex) {
      setLogoError(ex.response?.data?.error || 'Failed to remove logo.');
      setLogoUploading(false);
    }
  };

  if (!currentOrg) {
    return (
      <div className="org-settings-page">
        <div className="org-settings-empty">No organization context.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="org-settings-page">
        <div className="org-settings-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="org-settings-page">

      {/* Header */}
      <header className="org-settings-header">
        <div className="eyebrow">◆ Organization</div>
        <h1 className="org-settings-name">{org?.name}</h1>
        <div className="org-settings-meta">
          {org?.domains?.length > 0 && (
            <div className="org-settings-meta-item">
              <Mail size={14} />
              <span>{org.domains.join(', ')}</span>
            </div>
          )}
          {org?.owner && (
            <div className="org-settings-meta-item">
              <Crown size={14} />
              <span>Owned by {org.owner.name}</span>
            </div>
          )}
          <div className="org-settings-meta-item">
            <UserIcon size={14} />
            <span>{org?.memberCount} member{org?.memberCount === 1 ? '' : 's'}</span>
          </div>
        </div>
      </header>

      {error && (
        <div className="org-settings-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Branding — logo upload */}
      {(isAdminPlus || org?.logo) && (
        <section className="org-settings-section">
          <header className="org-settings-section-header">
            <h2>Branding</h2>
          </header>

          <div className="org-branding">
            <div className="org-branding-preview">
              {org?.logo ? (
                <img
                  src={absoluteMediaUrl(org.logo)}
                  alt={`${org.name} logo`}
                />
              ) : (
                <div className="org-branding-empty">
                  <ImageIcon size={28} />
                  <span>No logo set</span>
                </div>
              )}
            </div>

            {isAdminPlus && (
              <div className="org-branding-controls">
                <label className={`btn btn-secondary btn-sm org-branding-upload-btn${logoUploading ? ' disabled' : ''}`}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    onChange={handleLogoUpload}
                    disabled={logoUploading}
                    hidden
                  />
                  <Upload size={14} />
                  {logoUploading ? 'Uploading…' : (org?.logo ? 'Replace logo' : 'Upload logo')}
                </label>

                {org?.logo && !logoUploading && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleLogoRemove}
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                )}

                <div className="org-branding-hint">
                  PNG, JPG, SVG or WebP. Up to 2&nbsp;MB. Rectangular is fine — it&rsquo;ll be scaled to fit.
                </div>

                {logoError && (
                  <div className="org-settings-error" style={{ marginTop: 12 }}>
                    <AlertCircle size={14} />
                    <span>{logoError}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Members */}
      <section className="org-settings-section">
        <header className="org-settings-section-header">
          <h2>Members</h2>
          <span className="org-settings-section-count">{members.length}</span>
        </header>

        <div className="org-settings-members">
          {members.map(m => {
            const target = m.user;
            const isSelf = String(target._id) === String(user._id);
            const isOwnerRow = m.role === 'owner';

            // Can THIS caller modify THIS row?
            const canModify = isAdminPlus
              && !isOwnerRow                                   // owner is protected
              && (isOwner || m.role !== 'admin' || isSelf);    // admin can't touch other admins

            /*
             * WR-023: removal is narrower than role editing.
             *
             * The owner cannot be removed (ownership must be transferred
             * first), and nobody can remove themselves — since removal now
             * deactivates the account, self-removal would revoke your own
             * login and tokens mid-request. Both are enforced server-side;
             * these are the reasons shown in the tooltip so the disabled
             * button explains itself instead of just being dead.
             */
            const removeBlockedReason =
              isOwnerRow ? 'The organization owner cannot be removed. Transfer ownership first.'
              : isSelf   ? 'You cannot remove yourself. Ask the owner to remove you.'
              : (m.role === 'admin' && !isOwner) ? 'Only the owner can remove another admin.'
              : null;

            const canRemove = isAdminPlus && !removeBlockedReason;

            return (
              <div key={m._id} className="member-row">
                {/* WR-018 §2: was `origin + target.avatar` — an unsigned URL,
                    which 401s since WR-014 made /uploads private, leaving the
                    browser to paint alt={target.name} inside the circle.
                    UserAvatar signs the path and falls back to initials. */}
                <UserAvatar user={target} className="member-avatar" />

                <div className="member-info">
                  <div className="member-name">
                    {target.name}
                    {isSelf && <span className="member-self">you</span>}
                  </div>
                  <div className="member-email">{target.email}</div>
                </div>

                <div className="member-meta">
                  {m.joinedAt && (
                    <span className="member-joined">
                      Joined {format(new Date(m.joinedAt), 'MMM yyyy')}
                    </span>
                  )}
                </div>

                <div className="member-role">
                  {canModify && m.role !== 'owner' ? (
                    <select
                      value={m.role}
                      onChange={e => handleRoleChange(m, e.target.value)}
                      className="member-role-select"
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                    </select>
                  ) : (
                    <span className={`role-badge role-${m.role}`}>
                      <RoleIcon role={m.role} />
                      {m.role}
                    </span>
                  )}
                </div>

                <div className="member-actions">
                  {isAdminPlus ? (
                    <button
                      className="btn btn-ghost btn-sm member-remove-btn"
                      onClick={() => setRemoving(m)}
                      disabled={!canRemove}
                      title={removeBlockedReason || 'Remove from organization'}
                    >
                      <UserMinus size={14} />
                    </button>
                  ) : <span className="member-actions-placeholder" />}
                </div>
              </div>
            );
          })}
        </div>

        {!isAdminPlus && (
          <div className="org-settings-readonly-note">
            You're viewing this list as a member. Only admins and the owner can change roles or remove people.
          </div>
        )}
      </section>

      {/*
        ── Former members (WR-023 §3) ──────────────────────────────────
        Collapsed by default and rendered only when there is something in
        it, so a healthy organization never sees the section at all. It is
        an audit surface and an undo, not part of the day-to-day page —
        which is also why it is admin-only.
      */}
      {isAdminPlus && formerMembers.length > 0 && (
        <section className="org-settings-section">
          <header
            className="org-settings-section-header org-settings-section-collapsible"
            onClick={() => setShowFormer(v => !v)}
            role="button"
            tabIndex={0}
            aria-expanded={showFormer}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowFormer(v => !v); }
            }}
          >
            {showFormer ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <h2>Former members</h2>
            <span className="org-settings-section-count">{formerMembers.length}</span>
          </header>

          {showFormer && (
            <>
              <div className="org-settings-former-note">
                These people have been removed from {org?.name || 'this organization'}. They
                cannot sign in, hold no project memberships and have no working access tokens.
                Work they authored stays credited to them.
              </div>

              <div className="org-settings-members">
                {formerMembers.map(f => (
                  <div key={f._id} className="member-row member-row-former">
                    <UserAvatar user={f} className="member-avatar" />

                    <div className="member-info">
                      <div className="member-name">
                        {f.name}
                        <span className="member-former-badge">former member</span>
                      </div>
                      <div className="member-email">{f.email}</div>
                    </div>

                    <div className="member-meta">
                      {f.deactivatedAt && (
                        <span className="member-joined">
                          Removed {formatDistanceToNow(new Date(f.deactivatedAt), { addSuffix: true })}
                          {f.deactivatedBy?.name ? ` by ${f.deactivatedBy.name}` : ''}
                        </span>
                      )}
                    </div>

                    <div className="member-role" />

                    <div className="member-actions">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleReactivate(f)}
                        disabled={String(reactivating) === String(f._id)}
                        title="Restore their ability to sign in. Memberships and tokens are not restored."
                      >
                        <RotateCcw size={14} />
                        <span className="member-reactivate-label">
                          {String(reactivating) === String(f._id) ? 'Reactivating…' : 'Reactivate'}
                        </span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Invites */}
      {isAdminPlus && (
        <section className="org-settings-section">
          <header className="org-settings-section-header">
            <h2>Invites</h2>
            <span className="org-settings-section-count">{invites.length}</span>
            <button
              className="btn btn-primary btn-sm org-settings-section-action"
              onClick={() => setShowInviteModal(true)}
            >
              <Plus size={14} />
              Generate invite
            </button>
          </header>

          {/* Just-generated invite — shown prominently with the URL */}
          {justGenerated && (
            <div className="invite-fresh">
              <div className="invite-fresh-label">
                <Check size={14} />
                Invite created. Share this link manually:
              </div>
              <div className="invite-fresh-url">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl(justGenerated.token)}
                  onFocus={e => e.target.select()}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleCopy(justGenerated.token)}
                >
                  {copiedToken === justGenerated.token
                    ? <><Check size={14} /> Copied</>
                    : <><Copy size={14} /> Copy</>}
                </button>
              </div>
              <div className="invite-fresh-meta">
                {justGenerated.email
                  ? <>For <strong>{justGenerated.email}</strong> · {justGenerated.role} · </>
                  : <>Open invite · {justGenerated.role} · </>}
                Expires {format(new Date(justGenerated.expiresAt), 'MMM d, yyyy')}
              </div>
              <button
                className="invite-fresh-dismiss"
                onClick={() => setJustGenerated(null)}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Pending invites list */}
          <div className="invites-list">
            {invites.length === 0 ? (
              <div className="invites-empty">No pending invites.</div>
            ) : invites.map(inv => (
              <div key={inv._id} className="invite-row">
                <div className="invite-icon"><LinkIcon size={16} /></div>

                <div className="invite-info">
                  <div className="invite-target">
                    {inv.email || <span className="invite-open">Open invite</span>}
                  </div>
                  <div className="invite-meta">
                    <span className={`role-badge role-${inv.role}`}>
                      <RoleIcon role={inv.role} />
                      {inv.role}
                    </span>
                    <span className="invite-expires">
                      <Clock size={11} />
                      Expires {formatDistanceToNow(new Date(inv.expiresAt), { addSuffix: true })}
                    </span>
                    {inv.createdBy && (
                      <span className="invite-creator">
                        by {inv.createdBy.name}
                      </span>
                    )}
                  </div>
                </div>

                <div className="invite-actions">
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleCopy(inv.token)}
                    title="Copy link"
                  >
                    {copiedToken === inv.token
                      ? <Check size={14} />
                      : <Copy size={14} />}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm invite-revoke-btn"
                    onClick={() => handleRevoke(inv.token)}
                    title="Revoke"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Integrations — Slack (WR-020) */}
      {isAdminPlus && (
        <section className="org-settings-section">
          <header className="org-settings-section-header">
            <h2>Integrations</h2>
          </header>
          <SlackIntegration
            orgId={currentOrg._id}
            config={slack}
            onChange={setSlack}
          />
        </section>
      )}

      {showInviteModal && (
        <GenerateInviteModal
          onClose={() => setShowInviteModal(false)}
          onCreate={handleGenerate}
        />
      )}

      {/* WR-023 — impact-first removal confirmation. */}
      {removing && (
        <RemoveMemberDialog
          org={currentOrg}
          membership={removing}
          members={members}
          onCancel={() => setRemoving(null)}
          onConfirm={handleRemoveConfirmed}
        />
      )}

    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// SlackIntegration (WR-020) — inline, like the invite modal below
//
// One webhook per organization: everyone's task activity, one channel. The
// URL is a credential the server will not read back, so the input is always
// empty and always means "replace with this".
// ──────────────────────────────────────────────────────────────────────

const SLACK_EVENTS = [
  { key: 'taskAssigned',  label: 'Task assigned',  hint: 'Someone is put on a task' },
  { key: 'taskStarted',   label: 'Task started',   hint: 'Status moves to In progress' },
  { key: 'taskCompleted', label: 'Task completed', hint: 'Status moves to Done' },
  { key: 'taskCreated',   label: 'Task created',   hint: 'A new task appears on a board' },
  { key: 'commentAdded',  label: 'Comment added',  hint: 'Posts that a comment happened — never its text' },
];

function SlackIntegration({ orgId, config, onChange }) {
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  if (!config) return <div className="integration-loading">Loading integrations…</div>;

  const patch = async (body) => {
    setErr(null);
    setSaving(true);
    try {
      const res = await axios.put(`/orgs/${orgId}/integrations/slack`, body);
      onChange(res.data);
      return true;
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save the Slack settings.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!value) return;
    // Enable on first connect, so pasting a URL does something visible.
    const ok = await patch(config.configured ? { webhookUrl: value } : { webhookUrl: value, enabled: true });
    // Cleared either way: this field never holds a secret longer than the
    // request that carries it.
    setUrl('');
    if (ok) setTestResult(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`/orgs/${orgId}/integrations/slack/test`);
      setTestResult(res.data?.ok
        ? { ok: true, message: 'Test message delivered.' }
        : { ok: false, message: res.data?.error || 'Slack did not accept the message.' });
      // The attempt updates delivery health, so re-read it.
      const fresh = await axios.get(`/orgs/${orgId}/integrations/slack`);
      onChange(fresh.data);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.error || 'Failed to send the test message.' });
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect Slack?\n\nThe stored webhook URL is deleted and Bastion stops posting. ' +
      'You will need to paste the URL again to reconnect.'
    )) return;
    setErr(null);
    setSaving(true);
    try {
      const res = await axios.delete(`/orgs/${orgId}/integrations/slack`);
      onChange(res.data);
      setTestResult(null);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to disconnect.');
    } finally {
      setSaving(false);
    }
  };

  const revoked = config.status === 'revoked';
  const failing = config.status === 'error';

  return (
    <div className="integration-card">
      <div className="integration-head">
        <div className="integration-icon"><SlackIcon size={18} /></div>
        <div className="integration-title">
          <h3>Slack</h3>
          <p>
            Task activity from every project in this workspace, posted to the channel
            your incoming webhook points at. One integration for the whole organization —
            nobody has to set anything up individually.
          </p>
        </div>
        <span className={`integration-status integration-status-${
          !config.configured ? 'off' : revoked ? 'revoked' : failing ? 'error' : config.enabled ? 'on' : 'paused'
        }`}>
          {!config.configured ? 'Not connected'
            : revoked ? 'Webhook revoked'
            : failing ? 'Delivery failing'
            : config.enabled ? 'Connected' : 'Paused'}
        </span>
      </div>

      {revoked && (
        <div className="integration-alert integration-alert-bad">
          <AlertTriangle size={15} />
          <span>Webhook appears revoked — reconnect. Slack no longer recognises the stored URL, so posting has stopped. Create a new Incoming Webhook and paste it below.</span>
        </div>
      )}
      {failing && !revoked && (
        <div className="integration-alert">
          <AlertCircle size={15} />
          <span>{config.lastError || 'The last delivery failed.'}</span>
        </div>
      )}

      {/* Webhook URL — masked, replace-only */}
      <form className="integration-field" onSubmit={handleSaveUrl}>
        <label htmlFor="slack-webhook">Incoming webhook URL</label>
        {config.configured && (
          <div className="integration-current">
            <span className="integration-mask">{config.webhookMask}</span>
            <span className="integration-current-note">
              stored — it is never shown in full or sent back to this page
            </span>
          </div>
        )}
        <div className="integration-input-row">
          <input
            id="slack-webhook"
            type="password"
            autoComplete="off"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder={config.configured
              ? 'Paste a new URL to replace the stored one'
              : 'https://hooks.slack.com/services/…'}
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !url.trim()}>
            {config.configured ? 'Replace' : 'Connect'}
          </button>
        </div>
        <div className="integration-hint">
          Slack → your Bastion app → Incoming Webhooks → Add New Webhook to Workspace, then copy the URL.
        </div>
      </form>

      {err && (
        <div className="org-settings-error" style={{ marginTop: 12 }}>
          <AlertCircle size={16} />
          <span>{err}</span>
        </div>
      )}

      {config.configured && (
        <>
          <div className="integration-events">
            <div className="integration-events-title">Post to Slack when…</div>
            {SLACK_EVENTS.map(ev => (
              <label key={ev.key} className="integration-event">
                <input
                  type="checkbox"
                  checked={!!config.events?.[ev.key]}
                  disabled={saving}
                  onChange={e => patch({ events: { [ev.key]: e.target.checked } })}
                />
                <span className="integration-event-label">{ev.label}</span>
                <span className="integration-event-hint">{ev.hint}</span>
              </label>
            ))}
          </div>

          <div className="integration-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => patch({ enabled: !config.enabled })}
              disabled={saving}
            >
              {config.enabled ? 'Pause posting' : 'Resume posting'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleTest} disabled={testing}>
              <Send size={14} />
              {testing ? 'Sending…' : 'Send test message'}
            </button>
            <button className="btn btn-ghost btn-sm integration-disconnect" onClick={handleDisconnect} disabled={saving}>
              <Trash2 size={14} />
              Disconnect
            </button>
            {testResult && (
              <span className={`integration-test ${testResult.ok ? 'is-ok' : 'is-bad'}`}>
                {testResult.ok ? <Check size={14} /> : <AlertCircle size={14} />}
                {testResult.message}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// GenerateInviteModal — inline since it's only used here
// ──────────────────────────────────────────────────────────────────────

function GenerateInviteModal({ onClose, onCreate }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await onCreate({ email: email.trim() || null, role, expiresInDays });
    } catch (ex) {
      setErr(ex.response?.data?.error || 'Failed to create invite.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invite-modal" onClick={e => e.stopPropagation()}>
        <div className="invite-modal-header">
          <h3>Generate invite</h3>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: 6 }}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Bind to email <span className="invite-modal-optional">(optional)</span></label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="leave blank for an open invite"
              autoFocus
            />
            <div className="invite-modal-hint">
              If set, only that email address can use the link.
            </div>
          </div>

          <div className="input-group">
            <label>Role</label>
            <select value={role} onChange={e => setRole(e.target.value)}>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="input-group">
            <label>Expires in</label>
            <select value={expiresInDays} onChange={e => setExpiresInDays(+e.target.value)}>
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>

          {err && (
            <div className="org-settings-error" style={{ marginBottom: 12 }}>
              <AlertCircle size={16} />
              <span>{err}</span>
            </div>
          )}

          <div className="invite-modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Generating…' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OrgSettings;
